import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import { useMemo } from 'react'

import {
  type ExerciseItem,
  getDifficultyI18nKey,
  getMuscleI18nKey,
  getPrimaryMuscles,
  getToolI18nKey,
} from '@/utils/exercises'
import { getCanonicalUrl } from '@/utils/seo'

export const useExercise = (exercise: ExerciseItem) => {
  const { t } = useTranslation()
  const router = useRouter()
  const locale = router.locale || 'en'
  const tool = exercise.tool || 'bodyweight'

  const { title, description } = useMemo(() => {
    const primaryMuscleNames = getPrimaryMuscles(exercise.muscle_intensity)
      .slice(0, 3)
      .map((muscle) => t(getMuscleI18nKey(muscle.key)))
      .join(', ')

    return {
      title: t('landing_exercises_detail_meta_title', {
        name: exercise.title,
      }),
      description: t('landing_exercises_detail_meta_description', {
        name: exercise.title,
        muscles: primaryMuscleNames || 'multiple muscles',
        difficulty: t(getDifficultyI18nKey(exercise.skill_required)),
        tool: t(getToolI18nKey(exercise.tool)),
      }),
    }
  }, [
    exercise.muscle_intensity,
    exercise.skill_required,
    exercise.title,
    exercise.tool,
    t,
  ])

  const { breadcrumbs, breadcrumbsJsonLd } = useMemo(() => {
    const breadcrumbs = [
      { label: t('landing_exercises_breadcrumb_home'), href: '/' },
      {
        label: t('landing_exercises_breadcrumb_exercises'),
        href: '/exercises',
      },
      {
        label: t(getToolI18nKey(exercise.tool)),
        href: `/exercises?equipment=${tool}`,
      },
      { label: exercise.title, href: `/exercises/${tool}/${exercise.slug}` },
    ]

    const breadcrumbsJsonLd = breadcrumbs.map((breadcrumb, index) => ({
      position: index + 1,
      name: breadcrumb.label,
      item: getCanonicalUrl(
        breadcrumb.href,
        locale === 'en' ? undefined : locale
      ),
    }))

    return {
      breadcrumbs,
      breadcrumbsJsonLd,
    }
  }, [exercise.slug, exercise.title, exercise.tool, locale, t, tool])

  return {
    title,
    description,
    breadcrumbs,
    breadcrumbsJsonLd,
  }
}
